import { useState } from "react";
import { DoorOpen } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNumericKeypadField } from "@/components/numeric-keypad/useNumericKeypadField";
import { centsToFixedString, toCentsFromString } from "@/lib/currency-cents";

interface OpenCashRegisterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (amount: number) => Promise<void>;
}

export function OpenCashRegisterDialog({
  open,
  onOpenChange,
  onConfirm,
}: OpenCashRegisterDialogProps) {
  const [amount, setAmount] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openingAmountField = useNumericKeypadField({
    id: "cash.opening_amount",
    label: "Valor Inicial (Fundo de Troco)",
    mode: "currency",
    getCents: () => toCentsFromString(amount),
    setCents: (c) => setAmount(centsToFixedString(c)),
    maxCents: 999_999_99,
  });

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm(parseFloat(amount) || 0);
      setAmount("");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DoorOpen className="h-5 w-5" />
            Abrir Caixa
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="openingAmount">Valor Inicial (Fundo de Troco)</Label>
            <Input
              id="openingAmount"
              type="number"
              min="0"
              step="0.01"
              placeholder="R$ 0,00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onFocus={openingAmountField.activate}
              onClick={openingAmountField.activate}
              readOnly={openingAmountField.readOnly}
              inputMode={openingAmountField.inputMode}
              autoFocus
            />
            <p className="text-sm text-muted-foreground">
              Informe o valor em dinheiro que está no caixa ao iniciar o turno.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={isSubmitting}>
            {isSubmitting ? "Abrindo..." : "Abrir Caixa"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
